import React, { useEffect, useState } from "react";
import {
  Trash2,
  CheckCircle,
  Phone,
  Mail,
  Search,
  Filter,
  Download,
  RefreshCw,
  MoreVertical,
  Eye,
  Clock,
  TrendingUp,
} from "lucide-react";
import {
  Menu,
  MenuItem,
  IconButton,
  Chip,
  Avatar,
  CircularProgress,
  Tooltip,
  Badge,
} from "@mui/material";

const AdminInquiryPanel = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedInquiry, setSelectedInquiry] = useState(null);

  const fetchInquiries = async () => {
    setLoading(true);
    try {
      const res = await fetch("https://archpoint.onrender.com/api/inquiry");
      const data = await res.json();
      setInquiries(data);
    } catch (error) {
      console.error("Error fetching inquiries:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInquiries();
  }, []);

  const updateStatus = async (id, status) => {
    await fetch(`https://archpoint.onrender.com/api/inquiry/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    });
    fetchInquiries();
    handleCloseMenu();
  };

  const deleteInquiry = async (id) => {
    if (!window.confirm("Are you sure you want to delete this inquiry?")) return;
    await fetch(`https://archpoint.onrender.com/api/inquiry/${id}`, {
      method: "DELETE",
    });
    fetchInquiries();
    handleCloseMenu();
  };

  const handleOpenMenu = (event, inquiry) => {
    setAnchorEl(event.currentTarget);
    setSelectedInquiry(inquiry);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedInquiry(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "new":
        return { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" };
      case "contacted":
        return { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200" };
      case "completed":
        return { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200" };
      default:
        return { bg: "bg-gray-50", text: "text-gray-700", border: "border-gray-200" };
    }
  };

  const stats = [
    {
      label: "Total Inquiries",
      value: inquiries.length,
      icon: <Mail className="w-5 h-5" />,
      color: "from-blue-500 to-blue-600",
    },
    {
      label: "New",
      value: inquiries.filter((i) => i.status === "new").length,
      icon: <Clock className="w-5 h-5" />,
      color: "from-amber-500 to-amber-600",
    },
    {
      label: "In Progress",
      value: inquiries.filter((i) => i.status === "contacted").length,
      icon: <TrendingUp className="w-5 h-5" />,
      color: "from-purple-500 to-purple-600",
    },
    {
      label: "Completed",
      value: inquiries.filter((i) => i.status === "completed").length,
      icon: <CheckCircle className="w-5 h-5" />,
      color: "from-emerald-500 to-emerald-600",
    },
  ];

  const filteredInquiries = inquiries.filter((inq) => {
    const matchesSearch =
      inq.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inq.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inq.phone.includes(searchTerm);
    const matchesFilter = filterStatus === "all" || inq.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
                Inquiry Management
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Monitor and manage all customer inquiries
              </p>
            </div>
            <div className="flex gap-3">
              <Tooltip title="Refresh">
                <button
                  onClick={fetchInquiries}
                  className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all shadow-sm"
                >
                  <RefreshCw className="w-4 h-4" />
                  <span className="hidden sm:inline">Refresh</span>
                </button>
              </Tooltip>
              {/* <Tooltip title="Export Data">
                <button className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-md hover:shadow-lg">
                  <Download className="w-4 h-4" />
                  <span className="hidden sm:inline">Export</span>
                </button>
              </Tooltip> */}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all p-6 border border-gray-100"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">
                    {stat.label}
                  </p>
                  <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                </div>
                <div
                  className={`w-14 h-14 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center text-white shadow-lg`}
                >
                  {stat.icon}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Search and Filter */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6 border border-gray-100">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setFilterStatus("all")}
                className={`px-5 py-3 rounded-lg font-medium transition-all ${
                  filterStatus === "all"
                    ? "bg-blue-600 text-white shadow-md"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                All
              </button>
              <button
                onClick={() => setFilterStatus("new")}
                className={`px-5 py-3 rounded-lg font-medium transition-all ${
                  filterStatus === "new"
                    ? "bg-amber-600 text-white shadow-md"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                New
              </button>
              <button
                onClick={() => setFilterStatus("contacted")}
                className={`px-5 py-3 rounded-lg font-medium transition-all ${
                  filterStatus === "contacted"
                    ? "bg-purple-600 text-white shadow-md"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                In Progress
              </button>
              <button
                onClick={() => setFilterStatus("completed")}
                className={`px-5 py-3 rounded-lg font-medium transition-all ${
                  filterStatus === "completed"
                    ? "bg-emerald-600 text-white shadow-md"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Completed
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <CircularProgress />
            </div>
          ) : filteredInquiries.length === 0 ? (
            <div className="text-center py-20">
              <Mail className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No inquiries found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Contact Details
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredInquiries.map((inq, index) => {
                    const statusColors = getStatusColor(inq.status);
                    return (
                      <tr
                        key={inq._id}
                        className="hover:bg-blue-50/50 transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Avatar
                              sx={{
                                bgcolor: `hsl(${index * 137.5}, 70%, 50%)`,
                                width: 40,
                                height: 40,
                              }}
                            >
                              {inq.name.charAt(0).toUpperCase()}
                            </Avatar>
                            <div className="ml-4">
                              <div className="text-sm font-semibold text-gray-900">
                                {inq.name}
                              </div>
                              <div className="text-xs text-gray-500 max-w-xs truncate">
                                {inq.projectDetails || "No details provided"}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            <div className="flex items-center text-sm text-gray-700">
                              <Mail className="w-4 h-4 mr-2 text-gray-400" />
                              {inq.email}
                            </div>
                            <div className="flex items-center text-sm text-gray-700">
                              <Phone className="w-4 h-4 mr-2 text-gray-400" />
                              {inq.phone}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold border ${statusColors.bg} ${statusColors.text} ${statusColors.border}`}
                          >
                            {inq.status === "new" && "🆕 New"}
                            {inq.status === "contacted" && "🔄 In Progress"}
                            {inq.status === "completed" && "✅ Completed"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {new Date(inq.createdAt).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Tooltip title="Mark as Contacted">
                              <button
                                onClick={() =>
                                  updateStatus(inq._id, "contacted")
                                }
                                className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-all"
                              >
                                <CheckCircle className="w-5 h-5" />
                              </button>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <button
                                onClick={() => deleteInquiry(inq._id)}
                                className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-all"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </Tooltip>
                            <IconButton
                              onClick={(e) => handleOpenMenu(e, inq)}
                              size="small"
                            >
                              <MoreVertical className="w-5 h-5" />
                            </IconButton>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Context Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
          PaperProps={{
            elevation: 3,
            sx: {
              minWidth: 200,
              borderRadius: 2,
            },
          }}
        >
          <MenuItem
            onClick={() =>
              selectedInquiry && updateStatus(selectedInquiry._id, "new")
            }
          >
            <Clock className="w-4 h-4 mr-2" />
            Mark as New
          </MenuItem>
          <MenuItem
            onClick={() =>
              selectedInquiry && updateStatus(selectedInquiry._id, "contacted")
            }
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Mark as Contacted
          </MenuItem>
          <MenuItem
            onClick={() =>
              selectedInquiry && updateStatus(selectedInquiry._id, "completed")
            }
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Mark as Completed
          </MenuItem>
          <MenuItem
            onClick={() => selectedInquiry && deleteInquiry(selectedInquiry._id)}
            sx={{ color: "error.main" }}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete Inquiry
          </MenuItem>
        </Menu>
      </div>
    </div>
  );
};

export default AdminInquiryPanel;